'use client';

import { memo, useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { Search, Shield, Building2, BadgeDollarSign, Upload, Calculator } from 'lucide-react';
import { lookupHref } from '@/lib/upload-handoff';

// 2026-05-28 (Cortana PR #192): MedicationAutocomplete pulls in medications.ts
// (672 KB / 17k lines) + brandNameIndex. Eagerly importing it ballooned the
// home-page JS bundle by 180 KB (the Lighthouse 'reduce unused JS' warning).
// Lazy-loading defers that until the user actually clicks into the search box.
//
// LCP impact: 6.5s mobile → expected ~2.5s
// TBT impact: 470ms mobile → expected ~150ms
const MedicationAutocomplete = lazy(() =>
  import('./MedicationAutocomplete').then((m) => ({ default: m.MedicationAutocomplete }))
);

// Lightweight placeholder while the real autocomplete loads.
// Identical visual height so CLS = 0.
const SearchPlaceholder = memo(() => {
  const { t } = useTranslation();
  return (
    <div className="relative">
      <div className="h-14 sm:h-16 rounded-xl border border-border bg-card flex items-center px-4 sm:px-5 gap-3">
        <Search className="h-5 w-5 text-muted-foreground flex-shrink-0" aria-hidden="true" />
        <span className="text-base text-muted-foreground select-none">{t('hero.search_loading')}</span>
      </div>
    </div>
  );
});
SearchPlaceholder.displayName = 'SearchPlaceholder';

const PLACEHOLDER_DRUGS = ['Ozempic', 'Wegovy', 'Metformin', 'Lisinopril', 'Atorvastatin', 'Amoxicillin'];
const ROTATION_INTERVAL_MS = 2800; // legacy fade interval; superseded by typewriter timings below

// Typewriter animation timings - restored 2026-06-03 per Admiral directive.
// Idle-deferred start (uses requestIdleCallback) means LCP isn't affected.
// All timers cleared on unmount; animation pauses if the user focuses or types.
const TYPE_CHAR_MS = 80;        // ms per character while typing forward
const BACKSPACE_CHAR_MS = 50;   // ms per character while deleting (faster than typing)
const HOLD_FULL_MS = 1600;      // ms to hold the full word before deleting
const HOLD_EMPTY_MS = 250;      // ms to pause empty before next drug starts

const StatValue = memo(({ value, label }: { value: string; label: string }) => (
  <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground text-center">
    <span className="tabular-nums font-display font-bold gradient-text">{value}</span>{' '}
    <span>{label}</span>
  </p>
));
StatValue.displayName = 'StatValue';

// DS9 Odo audit: fake rotating stats removed (were $12.4M saved, 500 pharmacies,
// 73% savings, 45K prescriptions - all fabricated before first real user).
// Replaced with verified platform facts only.
// 2026-06-03 Cortana: keys (free / no_account / three_min / verified) translated via DeepL
// into the 5 Tier A locales. Values & labels read from hero.stats.* at render time.
const DYNAMIC_STAT_KEYS: { value: string; label: string }[] = [
  { value: 'hero.stats.free_value',       label: 'hero.stats.free_label' },
  { value: 'hero.stats.no_account_value', label: 'hero.stats.no_account_label' },
  { value: 'hero.stats.three_min_value',  label: 'hero.stats.three_min_label' },
  { value: 'hero.stats.verified_value',   label: 'hero.stats.verified_label' },
];

const DynamicStatLine = memo(() => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const id = 'requestIdleCallback' in window
      ? (window as any).requestIdleCallback(() => setStarted(true), { timeout: 3000 })
      : setTimeout(() => setStarted(true), 2000);
    return () => {
      if ('cancelIdleCallback' in window) (window as any).cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, []);

  useEffect(() => {
    if (!started) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % DYNAMIC_STAT_KEYS.length);
    }, 4200);
    return () => clearInterval(timer);
  }, [started]);

  const stat = DYNAMIC_STAT_KEYS[index];

  return (
    <div className="h-10 sm:h-12 flex items-center justify-center overflow-hidden">
      <StatValue value={t(stat.value)} label={t(stat.label)} />
    </div>
  );
});
DynamicStatLine.displayName = 'DynamicStatLine';

const HeroSearchBar = memo(({ onSearch }: { onSearch: (q: string) => void }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  // typedChars = how much of the current drug name to show in the placeholder.
  // 0 → empty placeholder. 7 with index=0 (Ozempic) → full word.
  const [typedChars, setTypedChars] = useState(0);
  const [rotationStarted, setRotationStarted] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Defer animation start until idle - protects LCP
  useEffect(() => {
    const id = 'requestIdleCallback' in window
      ? (window as any).requestIdleCallback(() => setRotationStarted(true), { timeout: 3000 })
      : setTimeout(() => setRotationStarted(true), 2000);
    return () => {
      if ('cancelIdleCallback' in window) (window as any).cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, []);

  // Direction state - ref to avoid extra renders. Drives a clean state machine
  // (paused → typing → holding → deleting → next drug). Re-renders only when
  // typedChars or placeholderIndex change, not on every direction flip.
  const directionRef = useRef<'typing' | 'holding' | 'deleting' | 'paused'>('typing');

  // Typewriter loop - restored 2026-06-03 per Admiral directive.
  // Per-character typing, hold on full word, backspace, advance to next drug.
  // Pauses cleanly when the user focuses or types.
  useEffect(() => {
    if (!rotationStarted) return;
    if (isFocused || query.length > 0) {
      directionRef.current = 'paused';
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    const drug = PLACEHOLDER_DRUGS[placeholderIndex];
    const dir = directionRef.current;

    if (dir === 'paused') {
      // Resume from empty
      directionRef.current = 'typing';
      setTypedChars(0);
      return;
    }

    if (dir === 'typing') {
      if (typedChars < drug.length) {
        timerRef.current = setTimeout(() => setTypedChars(c => c + 1), TYPE_CHAR_MS);
      } else {
        directionRef.current = 'holding';
        timerRef.current = setTimeout(() => {
          directionRef.current = 'deleting';
          setTypedChars(c => c - 1);
        }, HOLD_FULL_MS);
      }
    } else if (dir === 'deleting') {
      if (typedChars > 0) {
        timerRef.current = setTimeout(() => setTypedChars(c => c - 1), BACKSPACE_CHAR_MS);
      } else {
        // Empty - advance to next drug and switch direction
        timerRef.current = setTimeout(() => {
          setPlaceholderIndex(p => (p + 1) % PLACEHOLDER_DRUGS.length);
          directionRef.current = 'typing';
        }, HOLD_EMPTY_MS);
      }
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [rotationStarted, placeholderIndex, typedChars, isFocused, query]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (trimmed.length >= 2) {
      onSearch(trimmed);
    } else {
      onSearch(PLACEHOLDER_DRUGS[placeholderIndex]);
    }
  }, [query, placeholderIndex, onSearch]);

  // Build the current animated placeholder text. A blinking caret │ is appended at the end
  // to give the typewriter feel. Translation wrapper from hero.search_placeholder is bypassed
  // for the animated case - we re-use the same key but substitute the partial drug name.
  const drug = PLACEHOLDER_DRUGS[placeholderIndex];
  const partial = drug.slice(0, typedChars);
  const currentPlaceholder = t('hero.search_placeholder', { drug: partial });

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full max-w-2xl mx-auto"
      role="search"
      aria-label={t('hero.search_aria_section')}
    >
      <div className="relative group">
        {/* Glow - use opacity transition only, no blur recalc on hover */}
        <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-primary/20 via-secondary/15 to-primary/20 blur-lg opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-500 pointer-events-none" />

        {/* Removed backdrop-blur-xl - saves ~4ms/frame on mobile compositing */}
        <div className="relative flex items-center rounded-2xl border-2 border-primary/20 bg-card/95 shadow-2xl shadow-primary/10 focus-within:border-primary/50 focus-within:shadow-primary/20 transition-[border-color,box-shadow] duration-300 overflow-hidden">
          <Search className="absolute left-5 h-6 w-6 text-muted-foreground pointer-events-none" aria-hidden="true" />

          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={currentPlaceholder}
            className={`
              w-full h-14 sm:h-16 md:h-[72px] pl-12 sm:pl-14 pr-[120px] sm:pr-[160px] md:pr-48
              bg-transparent text-sm sm:text-base md:text-xl text-foreground
              placeholder:text-muted-foreground/60
              focus:outline-none
            `}
            aria-label={t('hero.search_aria_input')}
            autoComplete="off"
            spellCheck="false"
          />

          <button
            type="submit"
            className="absolute right-1.5 sm:right-2.5 h-10 sm:h-11 md:h-12 px-3 sm:px-6 md:px-8 rounded-xl bg-primary text-primary-foreground font-semibold text-xs sm:text-base hover:bg-primary/90 active:scale-[0.97] transition-all duration-200 shadow-lg shadow-primary/25"
          >
            {t('hero.search_submit')}
          </button>
        </div>
      </div>

      <p className="text-center text-sm text-muted-foreground/70 mt-3">
        {t('hero.search_subtitle')}
      </p>
    </form>
  );
});
HeroSearchBar.displayName = 'HeroSearchBar';

const TrustMicroSignals = memo(() => {
  const { t } = useTranslation();
  // 2026-09: three statements about how the product works. No counts, no
  // savings figures, no compliance badges until each can be evidenced.
  const signals = [
    { icon: Building2, text: t('hero.trust_licensed') },
    { icon: BadgeDollarSign, text: t('hero.trust_pay_pharmacy') },
    { icon: Shield, text: t('hero.trust_free') },
  ];
  return (
    <div className="flex flex-col items-center gap-4 mt-8">
      <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6">
        {signals.map(({ icon: Icon, text }, i) => (
          <div key={text} className="flex items-center gap-3 sm:gap-6">
            {i > 0 && <span className="hidden sm:block w-px h-4 bg-border" aria-hidden="true" />}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon className="h-4 w-4 text-secondary shrink-0" aria-hidden="true" />
              <span>{text}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});
TrustMicroSignals.displayName = 'TrustMicroSignals';

const Hero = memo(() => {
  const { t } = useTranslation();
  const router = useRouter();
  const [showDecorations, setShowDecorations] = useState(false);

  useEffect(() => {
    const id = 'requestIdleCallback' in window
      ? (window as any).requestIdleCallback(() => setShowDecorations(true), { timeout: 1500 })
      : setTimeout(() => setShowDecorations(true), 800);
    return () => {
      if ('cancelIdleCallback' in window) (window as any).cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, []);

  const handleSearch = useCallback((query: string) => {
    // In the fragment, not the query string: analytics record query strings (src/lib/upload-handoff.ts).
    router.push(lookupHref(query));
  }, [router]);

  return (
    // pt-16 keeps the headline clear of the fixed 64 px navbar: on a laptop
    // screen the hero is taller than 75vh and its first line sat under the bar.
    // The background layers are inset-0 and still run up behind the navbar.
    <section className="relative min-h-[70vh] md:min-h-[75vh] flex items-center justify-center overflow-hidden pt-16">
      {/* Background layers */}
      <div className="absolute inset-0 bg-aurora" />
      <div className="absolute inset-0 bg-dot-grid opacity-10" />
      <div className="absolute top-0 inset-x-0 h-32 bg-gradient-to-b from-background to-transparent z-[1]" />
      <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-t from-background to-transparent z-[1]" />

      {/* Deferred decorative blobs - no will-change to save GPU memory */}
      {showDecorations && (
        <>
          <div className="absolute top-1/4 left-1/6 w-[500px] h-[500px] bg-primary/12 rounded-full blur-[120px] animate-float-slow" />
          <div className="absolute bottom-1/4 right-1/6 w-[400px] h-[400px] bg-secondary/8 rounded-full blur-[100px] animate-float-slower" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[150px]" />
        </>
      )}

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-12 md:py-16">
        <div className="max-w-3xl mx-auto text-center space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-display font-bold tracking-tight leading-[1.08]">
            <span className="sr-only">{t('hero.seo_alt')}</span>
            <span aria-hidden="true">
              <span className="gradient-text-animated">{t('hero.title_line1')}</span>
              <br />
              <span className="text-foreground">{t('hero.title_line2')}</span>
            </span>
          </h1>

          <DynamicStatLine />

          <div className="pt-4">
            <Suspense fallback={<SearchPlaceholder />}>
              <MedicationAutocomplete onSearch={handleSearch} />
            </Suspense>
          </div>

          <div className="flex flex-col items-center gap-2 pt-2">
            <div className="flex items-center justify-center gap-3">
              <Link
                href="/upload-prescription"
                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 rounded-xl bg-primary text-primary-foreground text-base sm:text-lg font-bold shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 hover:brightness-110 active:scale-[0.97] transition-all"
              >
                <Upload className="h-5 w-5 sm:h-6 sm:w-6" />
                {t('hero.cta_upload_rx')}
              </Link>
              <Link
                href="/how-it-works"
                className="inline-flex items-center gap-2 px-4 sm:px-5 py-3 sm:py-4 rounded-xl bg-primary/10 text-primary border border-primary/20 text-sm font-semibold hover:bg-primary/20 active:scale-[0.97] transition-all"
              >
                <Calculator className="h-4 w-4" />
                {t('hero.cta_how_it_works')}
              </Link>
            </div>
          </div>

          <TrustMicroSignals />
        </div>
      </div>

      <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-background to-transparent z-[1]" />
    </section>
  );
});

Hero.displayName = 'Hero';
export default Hero;
